import { useMemo } from 'react'
import { useLocalStorage } from 'react-use'

import { isPlainObject } from 'utils/obj'

export const DEFAULT_SORT_CATEGORY = 'all'

export type SortOrder = 'asc' | 'desc'
export type StoredSorts = Record<string, { field: string; order: SortOrder }>

function isSortsValid(sorts: StoredSorts) {
  if (!isPlainObject(sorts)) {
    return false
  }

  // check that all entries in the object match the format we expect
  const invalidSorts = Object.entries(sorts).filter(([, sortOptions]) => {
    // check for valid sort order
    if (
      !sortOptions.order ||
      (sortOptions.order !== 'asc' && sortOptions.order !== 'desc')
    ) {
      return true
    }

    // check for valid sort field
    if (typeof sortOptions.field !== 'string' || !sortOptions.field) {
      return true
    }

    return false
  })

  return invalidSorts.length < 1
}

export function useStoredSorts(
  key,
  defaults
): [StoredSorts, (v: StoredSorts) => void] {
  const [sorts, setSorts] = useLocalStorage<StoredSorts>(key, defaults)

  // ensure stored values are in correct format
  // this handles converting a previous format
  // do this every time it's called as session storage could be changed at any time
  const actualSorts = useMemo(() => {
    if (sorts.field && sorts.order) {
      // this is just to get around the difference of the old sorting format vs what we have in TypeScript now
      const { field, order } = sorts as Record<string, any>
      const newSorts: StoredSorts = {
        [DEFAULT_SORT_CATEGORY]: { field, order }
      }
      // setSorts call is async so pass back the reformatted sorts now so the formatting difference doesn't cause an issue
      setSorts(newSorts)
      return newSorts
    } else if (!isSortsValid(sorts)) {
      // setSorts call is async so pass back the reformatted sorts now so the formatting difference doesn't cause an issue
      setSorts(defaults)
      return defaults
    }

    return sorts
  }, [sorts, setSorts, defaults])

  return [actualSorts, setSorts]
}
